import React, { useState } from 'react';

import CliveRecommendationPanel from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanel';
import CliveRecommendationPanelButton from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanelButton';
import { CliveStoreProcessingStatus } from '~/components/Clive/StatusChip/CliveStoreStatusChip';
import { CliveSummaryCard } from '~/components/Clive/SummaryCard/CliveSummaryCard';
import { CliveContentType } from '~/components/Clive/types';
import mixpanel from '~/components/CmsMain/mixpanel';
import FsIconButton from '~/components/core/FsWrappers/FsIconButton/FsIconButton';
import Text from '~/components/core/TextComponents/Text';
import RetryFailedUploadDialog from '~/components/Documents/UploadDocumentsCard/RetryFailedUploadDialog';
import CloseIcon from '~/components/icons/CloseIcon';
import ProgressTimeline from '~/components/ProgressTimeline/ProgressTimeline';
import type { CliveAction } from '~/components/types/claim-document-types';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type { Claim } from '~/server_shared/swagger/data-contracts';

import CliveInvoiceActionContainer from '../CliveInvoiceActionContainer';
import type { DocumentProgress } from '../hooks/documentProgressTypes';
import type { DocumentWithCliveInfo } from '../utils/cliveDocumentHelpers';
import { getClivePanelContent } from '../utils/cliveDocumentHelpers';

import { TEXTS } from './texts';
import type { LivePanelContent, LiveRun } from './types';
import { LIVE_RUN } from './types';

// Document type matching claim.documents (for CliveInvoiceActionContainer)
type ClaimDocument = NonNullable<ClaimPolymorph['documents']>[number];

/**
 * Creates a callback function for tracking document copy actions
 * @param document - Document object
 * @param claim - Claim object
 * @param contentType - Type of content being copied
 * @returns Callback function to track the mixpanel event
 */
const LIVE_PANEL: Record<LiveRun, LivePanelContent> = {
  [LIVE_RUN.PROCESSING]: {
    panelTitle: TEXTS.LIVE_PROCESSING_TITLE,
    panelSubtitle: TEXTS.LIVE_PROCESSING_SUBTITLE,
    avatarType: 'loading',
  },
  [LIVE_RUN.FAILED]: {
    panelTitle: TEXTS.LIVE_FAILED_TITLE,
    panelSubtitle: TEXTS.LIVE_FAILED_SUBTITLE,
    avatarType: 'error',
  },
};

/** The live run the panel speaks for, only while CMS's own status still says processing. */
const getLiveRun = (document: DocumentWithCliveInfo, progress?: DocumentProgress): LiveRun | null => {
  if (!progress || document.clive_store_information?.processing_status !== CliveStoreProcessingStatus.PROCESSING) {
    return null;
  }
  if (progress.isRunning) return LIVE_RUN.PROCESSING;
  return progress.isFailed ? LIVE_RUN.FAILED : null;
};

const createDocumentCopyCallback = (
  document: DocumentWithCliveInfo,
  claim: ClaimWithSubOrg,
  contentType: CliveContentType.SUMMARY
): (() => void) => {
  return () => {
    const eventName = MIXPANEL_EVENTS.DOCUMENT_SUMMARY_COPIED;
    mixpanel.track(eventName, {
      document_id: document.id,
      document_name: document.document_name,
      claim_id: claim.id,
      content_type: contentType,
    });
  };
};

interface ClaimWithSubOrg extends Claim {
  sub_organization_id?: number;
  policy?: {
    sub_organization_id?: number;
  };
}

interface DocumentSidePanelProps {
  document: DocumentWithCliveInfo;
  claim: ClaimWithSubOrg;
  onClose?: () => void;
  onDocumentUpdate?: (documentId?: number | null) => void;
  /** Live processing rows for this document, when the AI service has published any. */
  progress?: DocumentProgress;
}

export const DocumentSidePanel: React.FC<DocumentSidePanelProps> = ({
  document,
  claim,
  onClose,
  onDocumentUpdate,
  progress,
}) => {
  const [showEditDocument, setShowEditDocument] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);

  // Get document summary
  const documentSummary = document?.clive_store_information?.full_summary;

  // Get panel content configuration - handles all logic including invoice actions
  const panelContent = getClivePanelContent(document);
  const { showClassifyButton, showReviewButton, pendingInvoiceAction } = panelContent;
  const liveRun = getLiveRun(document, progress);
  const { panelTitle, panelSubtitle, avatarType } = liveRun ? LIVE_PANEL[liveRun] : panelContent;

  // Get full CliveAction from document for the dialog (helper only returns minimal info)
  const fullPendingInvoiceAction = pendingInvoiceAction
    ? ((document as { clive_actions?: CliveAction[] }).clive_actions?.find((a) => a.id === pendingInvoiceAction.id) as
        | CliveAction
        | undefined)
    : undefined;

  // Action component for the panel (button displayed inline with title)
  const handleClassifyClick = () => {
    mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_CLASSIFY_BUTTON_CLICKED, {
      document_id: document.id,
      document_name: document.document_name,
      claim_id: claim.id,
    });
    setShowEditDocument(true);
  };

  const handleInvoiceReviewClick = () => {
    setShowInvoiceDialog(true);
  };

  const handleInvoiceDialogClose = () => {
    setShowInvoiceDialog(false);
    onDocumentUpdate?.(null); // Trigger refresh to update clive_actions status
  };

  // Action component: "Review" for invoice action, "Classify" for classification needed
  const actionComponent = showReviewButton ? (
    <CliveRecommendationPanelButton text="Review" onClick={handleInvoiceReviewClick} />
  ) : (
    showClassifyButton && <CliveRecommendationPanelButton text="Classify" onClick={handleClassifyClick} />
  );

  return (
    <>
      <div className="h-full space-y-24 overflow-auto">
        <div className="flex items-center justify-between pl-24">
          <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD}>
            {document.document_name || 'Document Details'}
          </Text>
          {onClose && (
            <FsIconButton onClick={onClose} icon={CloseIcon} ignorePermissions={true} aria-label="Close dialog" />
          )}
        </div>

        <CliveRecommendationPanel
          title={panelTitle}
          subtitle={panelSubtitle}
          openByDefault={true}
          actionComponent={actionComponent}
          avatarType={avatarType}
        >
          {progress ? <ProgressTimeline entries={progress.entries} status={progress.status} /> : null}
          {documentSummary ? (
            <CliveSummaryCard
              title="Document Summary"
              summaryText={documentSummary}
              className="m-0"
              onCopySummary={createDocumentCopyCallback(document, claim, CliveContentType.SUMMARY)}
            />
          ) : null}
        </CliveRecommendationPanel>
      </div>

      {showEditDocument && showClassifyButton && (
        <RetryFailedUploadDialog
          open={showEditDocument}
          claim={claim as ClaimPolymorph}
          document={document}
          onCancel={() => setShowEditDocument(false)}
          onSuccess={() => {
            setShowEditDocument(false);
            onClose?.();
            onDocumentUpdate?.(null); // Trigger document list refresh
          }}
        />
      )}

      {showInvoiceDialog && fullPendingInvoiceAction && (
        <CliveInvoiceActionContainer
          document={document as unknown as ClaimDocument}
          action={fullPendingInvoiceAction}
          open={showInvoiceDialog}
          onClose={handleInvoiceDialogClose}
        />
      )}
    </>
  );
};

export default DocumentSidePanel;
